import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Container, Snackbar, Alert } from '@mui/material';
import {
    useCreatePositionMutation,
    useSavePositionDraftMutation,
    useDeployPositionMutation,
} from '../../../../api/preHQ';
import FormActions from './common/FormActions';
import ProgressIndicator from './common/ProgressIndicator';
import SuccessDialog from './common/SuccessDialog';
import { formSchema, WizardStep } from './common';
import TalentInsights from './talentInsights';
import JobDescription from './jobDescriptions';
import PreviewPage from './preview/index';
import PositionDetails from './positionDetails';

const CreatePosition = () => {
    const [activeStep, setActiveStep] = useState(WizardStep.POSITION_DETAILS);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeploying, setIsDeploying] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const navigate = useNavigate();

    // Redux Toolkit API hooks
    const [createPosition] = useCreatePositionMutation();
    const [savePositionDraft] = useSavePositionDraftMutation();
    const [deployPosition] = useDeployPositionMutation();

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            jobTitle: '',
            department: '',
            minYearsExp: 0,
            maxYearsExp: 0,
            location: { id: '', label: '' },
            hiringManager: { id: '', label: '' },
            bandLevel: '',
            budgetLowerLimit: 0,
            budgetMidLimit: 0,
            budgetUpperLimit: 0,
            mandatorySkills: [],
            optionalSkills: [],
            jobType: 'standard',
            description: '',
        },
    });

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case WizardStep.POSITION_DETAILS:
                return <PositionDetails form={form} />;
            case WizardStep.TALENT_INSIGHTS:
                return <TalentInsights form={form} />;
            case WizardStep.JOB_DESCRIPTION:
                return <JobDescription form={form} />;
            case WizardStep.PREVIEW_SUBMIT:
                return <PreviewPage form={form} />;
            default:
                return null;
        }
    };

    async function onSubmit(data) {
        setIsSaving(true);
        try {
            console.log('submit triggered:', data);
            // await createPosition(data).unwrap();
            setShowSuccessDialog(true);
            showSnackbar('Position created successfully!');
            console.log('Form data:', data);
        } catch (error) {
            console.error('Error creating position:', error);
            showSnackbar('Failed to create position. Please try again.', 'error');
        } finally {
            setIsSaving(false);
        }
    }

    const handleSaveAsDraft = async () => {
        const values = form.getValues();
        try {
            // await savePositionDraft(values).unwrap();
            showSnackbar('Draft saved successfully!');
            console.log('Draft data:', values);
        } catch (error) {
            console.error('Error saving draft:', error);
            showSnackbar('Failed to save draft. Please try again.', 'error');
        }
    };

    const handleDeploy = async () => {
        setIsDeploying(true);
        try {
            const values = form.getValues();
            // await deployPosition(values.positionId).unwrap();
            showSnackbar('Position deployed successfully!');
            navigate('/client/positions');
        } catch (error) {
            console.error('Error deploying position:', error);
            showSnackbar('Failed to deploy position. Please try again.', 'error');
        } finally {
            setIsDeploying(false);
        }
    };

    const handleNavigateToDashboard = () => {
        setShowSuccessDialog(false);
        navigate('/client/dashboard');
    };

    const handleStepChange = (nextStep) => {
        console.log('activeStep', activeStep, nextStep, activeStep, nextStep > activeStep);
        setCompletedSteps((prev) => [...prev, activeStep]);
        setActiveStep(nextStep);
    };

    return (
        <Box
            sx={{
                p: 3,
                bgcolor: '#f5f8fc',
                minHeight: '100vh',
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                        component="button"
                        sx={{
                            mr: 2,
                            p: 1,
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            bgcolor: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            '&:hover': {
                                bgcolor: '#f5f5f5',
                            },
                        }}
                        onClick={() => navigate('/client/positions')}
                    >
                        <ArrowBack sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography variant="h4" sx={{ color: '#3F455A', fontWeight: 500 }}>
                        Create New Position
                    </Typography>
                </Box>

                <ProgressIndicator
                    activeStep={activeStep}
                    completedSteps={completedSteps}
                    onStepClick={handleStepChange}
                />

                <Paper sx={{ p: 4, mt: 3, boxShadow: 1 }}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {renderStepContent()}
                            <FormActions
                                form={form}
                                onSaveAsDraft={handleSaveAsDraft}
                                isSaving={isSaving}
                                activeStep={activeStep}
                                setActiveStep={handleStepChange}
                                formValidate={() => form.formState.isValid}
                                formTriggerValidation={(fields) => form.trigger(fields)}
                                onSubmit={onSubmit}
                            />
                        </Box>
                    </form>
                </Paper>

                <SuccessDialog
                    open={showSuccessDialog}
                    onOpenChange={setShowSuccessDialog}
                    onDeploy={handleDeploy}
                    onDashboard={handleNavigateToDashboard}
                    isDeploying={isDeploying}
                />

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
};

export default CreatePosition;
